import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../services/api';

const DigitalMarketingPage = () => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', interest: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/leads', { ...form, source: 'Digital Marketing Course Page' });
      setSubmitted(true);
    } catch {
      alert('Something went wrong. Please try again.');
    }
  };

  const month1 = [
    { week: 'Week 1', title: 'Digital Marketing Fundamentals & SEO Basics', items: ['Intro to digital marketing & customer journey', 'Keyword research using Google Keyword Planner', 'On-page SEO (meta tags, URLs, structure)', 'Tools: Google Search Console & Analytics'] },
    { week: 'Week 2', title: 'Advanced SEO & Video Marketing', items: ['Off-page SEO & backlink strategies', 'Local SEO & Google Business Profile', 'YouTube channel setup & video SEO', 'Tools: SEMrush / Ahrefs overview'] },
    { week: 'Week 3', title: 'Social Media Marketing', items: ['Facebook & Instagram content strategy', 'Introduction to paid ads', 'LinkedIn marketing (B2B branding)', 'Twitter basics for business'] },
    { week: 'Week 4', title: 'Social Media Ads & Analytics', items: ['Facebook Ads setup & campaign structure', 'Audience targeting & retargeting', 'LinkedIn Ads basics', 'Google Analytics GA4 fundamentals'] },
  ];

  const month2 = [
    { week: 'Week 5', title: 'Google Ads (Search Campaigns)', items: ['PPC fundamentals', 'Campaign setup & keyword targeting', 'Ad copywriting & bidding strategies'] },
    { week: 'Week 6', title: 'Display Ads + Email Marketing', items: ['Google Display & YouTube Ads', 'Conversion tracking & optimization', 'Email marketing using Mailchimp'] },
    { week: 'Week 7', title: 'Live Project – Strategy Phase', items: ['Real business case study', 'Keyword research & content planning', 'Website audit & SEO strategy', 'Social media & campaign planning'] },
    { week: 'Week 8', title: 'Live Project – Execution Phase', items: ['Execute SEO & ad campaigns', 'Create social media content', 'Run basic ad campaigns', 'Analyze results & present report'] },
  ];

  const tools = ['Google Ads', 'Google Analytics (GA4)', 'Google Search Console', 'Facebook Ads Manager', 'LinkedIn Campaign Manager', 'Mailchimp', 'Canva', 'SEMrush / Ahrefs'];

  const faqs = [
    { q: 'Is this a digital marketing course with certificate?', a: 'Yes, you will receive a certificate after successful completion of the program.' },
    { q: 'Do I need prior experience?', a: 'No, the course starts from the basics and is suitable for complete beginners.' },
    { q: 'Is there practical training?', a: 'Yes, live project training is included in Month 2 with real business case studies.' },
    { q: 'What is the course duration?', a: '2 months (8 weeks) with structured weekly topics.' },
    { q: 'Will I get job support?', a: 'You will gain job-ready skills and practical experience to boost your career.' },
  ];

  return (
    <>
      <Helmet>
        <title>Digital Marketing Course with Certificate | 2-Month Program with Live Project</title>
        <meta name="description" content="Join digital marketing course with certificate. Learn SEO, Google Ads, Social Media & live projects in 2 months. Enroll now!" />
        <meta name="keywords" content="digital marketing course with certificate, digital marketing course, SEO course, Google Ads course, social media marketing course" />
        <link rel="canonical" href="https://besst.in/digital-marketing" />
        <meta property="og:title" content="Digital Marketing Course with Certificate | 2-Month Program with Live Project" />
        <meta property="og:description" content="Join digital marketing course with certificate. Learn SEO, Google Ads, Social Media & live projects in 2 months." />
        <meta property="og:url" content="https://besst.in/digital-marketing" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="BESST" />
      </Helmet>

      <div className="min-h-screen pt-32 pb-20">
        {/* Hero */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <div className="text-center max-w-4xl mx-auto">
            <div className="hero-badge mx-auto mb-6">📈 2-Month Program</div>
            <h1 className="text-4xl lg:text-5xl font-extrabold gradient-text mb-6 leading-tight">
              Digital Marketing Course with Certificate
            </h1>
            <p className="text-xl text-text-muted mb-4">Become Job-Ready in Just 2 Months with Live Project & Certification</p>
            <p className="text-text-muted mb-8">🔧 Live Project &nbsp;•&nbsp; 🛠️ Industry Tools &nbsp;•&nbsp; 📜 Certification Included</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#enroll" className="btn-primary px-8 py-4 text-lg font-bold rounded-xl">🚀 Enroll Now</a>
              <a href="#syllabus" className="px-8 py-4 text-lg font-bold rounded-xl border border-glass-border text-text-light hover:bg-white/5 transition-colors">
                📋 View Syllabus
              </a>
            </div>
            <p className="text-xs text-accent-gold mt-4">⚡ Limited seats available</p>
          </div>
        </div>

        {/* Course Highlights */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-4xl mx-auto">
            {[
              { icon: '📅', label: 'Duration', value: '8 Weeks (2 Months)' },
              { icon: '💼', label: 'Training', value: 'Live Project Included' },
              { icon: '💰', label: 'Course Fee', value: '₹18,000' },
              { icon: '📜', label: 'Certificate', value: 'On Completion' },
              { icon: '🧑‍🏫', label: 'Training', value: 'Expert-Led' },
            ].map(h => (
              <div key={h.label} className="glass-card text-center p-4">
                <div className="text-2xl mb-1">{h.icon}</div>
                <div className="text-text-muted text-xs mb-1">{h.label}</div>
                <div className="text-text-light font-bold text-sm">{h.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Syllabus */}
        <div id="syllabus" className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-3xl font-bold gradient-text text-center mb-10">Course Syllabus</h2>

          <div className="mb-8">
            <h3 className="text-xl font-bold text-accent-gold mb-6 text-center">🗓️ Month 1: Foundation + SEO + Social Media</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {month1.map(w => (
                <div key={w.week} className="glass-card">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-2 py-1 bg-primary/20 text-primary rounded text-xs font-bold">{w.week}</span>
                    <h4 className="text-text-light font-bold text-sm">{w.title}</h4>
                  </div>
                  <ul className="space-y-1">{w.items.map(i => <li key={i} className="text-text-muted text-xs flex items-start gap-2"><span className="text-primary mt-1">•</span>{i}</li>)}</ul>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xl font-bold text-accent-gold mb-6 text-center">🗓️ Month 2: Paid Ads + Live Project</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {month2.map(w => (
                <div key={w.week} className="glass-card">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-2 py-1 bg-accent-gold/20 text-accent-gold rounded text-xs font-bold">{w.week}</span>
                    <h4 className="text-text-light font-bold text-sm">{w.title}</h4>
                  </div>
                  <ul className="space-y-1">{w.items.map(i => <li key={i} className="text-text-muted text-xs flex items-start gap-2"><span className="text-accent-gold mt-1">•</span>{i}</li>)}</ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tools */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-2xl font-bold gradient-text text-center mb-8">🛠️ Tools You Will Master</h2>
          <div className="flex flex-wrap gap-3 justify-center max-w-3xl mx-auto">
            {tools.map(t => (
              <span key={t} className="px-4 py-2 bg-primary/20 text-primary rounded-full text-sm font-semibold border border-primary/30">{t}</span>
            ))}
          </div>
        </div>

        {/* Certification + Career */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="glass-card">
              <h3 className="text-xl font-bold text-text-light mb-4">🎓 Certification</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-bg-secondary rounded-xl">
                  <span className="text-2xl">📜</span>
                  <span className="text-text-light text-sm">Course Completion Certificate</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-bg-secondary rounded-xl">
                  <span className="text-2xl">💼</span>
                  <span className="text-text-light text-sm">Project Experience Certificate</span>
                </div>
              </div>
            </div>
            <div className="glass-card">
              <h3 className="text-xl font-bold text-text-light mb-4">📈 Career Opportunities</h3>
              <div className="space-y-2">
                {['Digital Marketing Executive', 'SEO Specialist', 'Social Media Manager', 'PPC / Google Ads Expert', 'Freelancer'].map(r => (
                  <div key={r} className="flex items-center gap-2 text-text-muted text-sm">
                    <span className="text-green-400">→</span>{r}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Who should join */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <div className="glass-card max-w-2xl mx-auto text-center">
            <h2 className="text-xl font-bold text-text-light mb-6">🎯 Who Should Join?</h2>
            <div className="flex flex-wrap gap-3 justify-center">
              {['Students & Freshers', 'Job Seekers', 'Entrepreneurs', 'Working Professionals', 'Freelancers'].map(t => (
                <span key={t} className="px-4 py-2 bg-accent-gold/20 text-accent-gold rounded-full text-sm font-semibold">{t}</span>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-2xl font-bold gradient-text text-center mb-8">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((f, i) => (
              <details key={i} className="glass-card">
                <summary className="font-semibold text-text-light cursor-pointer list-none flex justify-between items-center">
                  {f.q} <span className="text-primary text-xl">+</span>
                </summary>
                <p className="text-text-muted mt-3 text-sm">{f.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* Enroll Form */}
        <div id="enroll" className="max-w-[1400px] mx-auto px-4 sm:px-8">
          <div className="glass-card max-w-2xl mx-auto">
            {submitted ? (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="text-2xl font-bold text-text-light mb-2">Enrollment Request Received!</h3>
                <p className="text-text-muted">Our team will contact you within 24 hours with next steps.</p>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-text-light mb-2">Start Your Digital Marketing Career Today</h2>
                <p className="text-text-muted text-sm mb-6">⚡ Limited seats available — enroll now</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <input required placeholder="Full Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                    className="w-full px-4 py-3 bg-bg-secondary border border-glass-border rounded-xl text-text-light focus:outline-none focus:border-primary" />
                  <input required type="tel" placeholder="Phone Number *" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                    className="w-full px-4 py-3 bg-bg-secondary border border-glass-border rounded-xl text-text-light focus:outline-none focus:border-primary" />
                  <input required type="email" placeholder="Email Address *" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                    className="w-full px-4 py-3 bg-bg-secondary border border-glass-border rounded-xl text-text-light focus:outline-none focus:border-primary" />
                  <select required value={form.interest} onChange={e => setForm({...form, interest: e.target.value})}
                    className="w-full px-4 py-3 bg-bg-secondary border border-glass-border rounded-xl text-text-light focus:outline-none focus:border-primary">
                    <option value="">I am a... *</option>
                    <option>Student / Fresher</option>
                    <option>Job Seeker</option>
                    <option>Business Owner</option>
                    <option>Working Professional</option>
                  </select>
                  <button type="submit" className="w-full btn-primary py-4 text-lg font-bold">🚀 Enroll Now — ₹18,000</button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DigitalMarketingPage;
